import { 
  collection, 
  getDocs, 
  query, 
  where, 
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getBlockedDatesArray } from './blockedDaysService';

// Get minimum booking date (24 hours from now)
export const getMinimumBookingDate = () => {
  const minDate = new Date();
  minDate.setTime(minDate.getTime() + (24 * 60 * 60 * 1000)); // Add exactly 24 hours
  return minDate;
};

// Check if a specific product is available for a date range
export const checkProductAvailability = async (productId, startDate, endDate, location = 'All') => {
  try {
    // Format the dates to check (just the date part, not time)
    const targetStartDate = new Date(startDate);
    targetStartDate.setHours(0, 0, 0, 0);
    
    const targetEndDate = new Date(endDate);
    targetEndDate.setHours(23, 59, 59, 999);
    
    // Query orders collection for any bookings of this product that overlap with our date range
    const ordersRef = collection(db, 'orders');
    const snapshot = await getDocs(ordersRef);
    
    let conflictingOrders = [];
    
    for (const doc of snapshot.docs) {
      const orderData = doc.data();
      
      // Skip cancelled, failed, refunded, or draft orders
      const excludedStatuses = [
        'cancelled', 'Cancelled', 
        'failed', 'Failed',
        'refunded', 'Refunded',
        'draft', 'Draft',
        'error', 'Error'
      ];
      if (excludedStatuses.includes(orderData.status)) {
        continue;
      }
      
      // Check if this order contains the product we're checking
      const hasProduct = orderData.items && orderData.items.some(item => {
        // Method 1: Direct ID match (for system-generated orders)
        if (item.id === productId || item.productId === productId) {
          console.log(`✅ Found product match by ID: ${item.id || item.productId}`);
          return true;
        }
        
        // Method 2: Name-based matching for manual orders
        if (item.name && typeof productId === 'string') {
          const itemNameLower = item.name.toLowerCase();
          const productIdLower = productId.toLowerCase();
          
          // Direct name match
          if (itemNameLower === productIdLower) {
            console.log(`✅ Found product match by exact name: "${item.name}"`);
            return true;
          }
          
          // Enhanced Blue Marble matching for both manual and system orders
          if (itemNameLower.includes('blue marble') && 
              (productIdLower.includes('blue marble') || productIdLower.includes('blue_marble'))) {
            console.log(`✅ Found Blue Marble product match: "${item.name}"`);
            return true;
          }
        }
        
        return false;
      });
      
      if (!hasProduct) continue;
      
      // Get the rental period from the order
      const orderRentalPeriod = getRentalPeriodFromOrder(orderData);
      
      if (!orderRentalPeriod.startDate || !orderRentalPeriod.endDate) continue;
      
      // Check if the rental periods overlap
      const orderStart = new Date(orderRentalPeriod.startDate);
      orderStart.setHours(0, 0, 0, 0);
      
      const orderEnd = new Date(orderRentalPeriod.endDate);
      orderEnd.setHours(23, 59, 59, 999);
      
      // Check for overlap: target period overlaps with order period
      const hasOverlap = (
        (targetStartDate <= orderEnd) && (targetEndDate >= orderStart)
      );
      
      if (hasOverlap) {
        conflictingOrders.push({
          orderId: doc.id,
          orderData: orderData,
          conflictPeriod: {
            start: orderStart,
            end: orderEnd
          }
        });
      }
    }
    
    // Check for blocked days in the requested date range
    const blockedDaysConflicts = [];
    try {
      const blockedDates = await getBlockedDatesArray(startDate, endDate, location);
      
      if (blockedDates.length > 0) {
        console.log(`🚫 Found ${blockedDates.length} blocked dates: ${blockedDates.join(', ')}`);
        
        // Check if any blocked dates fall within our target date range
        const targetStart = targetStartDate.toISOString().split('T')[0];
        const targetEnd = targetEndDate.toISOString().split('T')[0];
        
        blockedDates.forEach(blockedDate => {
          if (blockedDate >= targetStart && blockedDate <= targetEnd) {
            blockedDaysConflicts.push({
              type: 'blocked_date',
              date: blockedDate,
              reason: 'Admin blocked date'
            });
          }
        });
      }
    } catch (error) {
      console.warn('Error checking blocked days, continuing availability check:', error);
      // Continue without blocked days check rather than failing completely
    }
    
    // Combine booking conflicts and blocked days conflicts
    const allConflicts = [...conflictingOrders, ...blockedDaysConflicts];
    
    return {
      available: allConflicts.length === 0,
      conflicts: allConflicts,
      bookingConflicts: conflictingOrders,
      blockedDaysConflicts: blockedDaysConflicts
    };
    
  } catch (error) {
    console.error('Error checking product availability:', error);
    throw new Error('Failed to check availability. Please try again.');
  }
};

// Enhanced helper function to extract rental period from order data
const getRentalPeriodFromOrder = (orderData) => {
  let startDate = null;
  let endDate = null;
  
  // Method 1: Check for new rentalPeriod field
  if (orderData.rentalPeriod?.startDate && orderData.rentalPeriod?.endDate) {
    startDate = orderData.rentalPeriod.startDate;
    endDate = orderData.rentalPeriod.endDate;
  }
  // Method 2: Check for explicit start/end dates
  else if (orderData.rentalStartDate && orderData.rentalEndDate) {
    startDate = orderData.rentalStartDate;
    endDate = orderData.rentalEndDate;
  }
  // Method 3: Check delivery info
  else if (orderData.deliveryInfo?.startDate && orderData.deliveryInfo?.endDate) {
    startDate = orderData.deliveryInfo.startDate;
    endDate = orderData.deliveryInfo.endDate;
  }
  // Method 4: Check delivery object
  else if (orderData.delivery?.startDate && orderData.delivery?.endDate) {
    startDate = orderData.delivery.startDate;
    endDate = orderData.delivery.endDate;
  }
  // Method 5: Check items array for rental periods
  else if (orderData.items && orderData.items.length > 0) {
    for (const item of orderData.items) {
      if (item.startDate && item.endDate) {
        startDate = item.startDate;
        endDate = item.endDate;
        break;
      }
    }
    
    // Fallback: if only single date exists on items, assume 1-day rental
    if (!startDate && !endDate) {
      const firstItem = orderData.items[0];
      if (firstItem.date) {
        startDate = firstItem.date;
        endDate = firstItem.date;
      }
    }
  }
  
  // Method 6: MANUAL ORDERS - Check root level rentalDate
  if (!startDate && orderData.rentalDate) {
    startDate = orderData.rentalDate;
    endDate = orderData.rentalDate; // Same day rental for manual orders
    console.log(`📅 Manual order detected: using rentalDate ${orderData.rentalDate}`);
  }
  
  // Method 7: Fallback to other legacy fields
  if (!startDate) {
    const legacyDate = orderData.deliveryDate || orderData.delivery?.date;
    if (legacyDate) {
      startDate = legacyDate;
      endDate = legacyDate;
    }
  }
  
  return { startDate, endDate };
};

// Check availability for multiple products on a date range
export const checkMultipleProductsAvailability = async (productIds, startDate, endDate) => {
  try {
    const results = {};
    
    for (const productId of productIds) {
      results[productId] = await checkProductAvailability(productId, startDate, endDate);
    }
    
    return results;
  } catch (error) {
    console.error('Error checking multiple products availability:', error);
    throw new Error('Failed to check availability. Please try again.');
  }
};

// Get unavailable date ranges for a product within a date range
export const getUnavailableDateRanges = async (productId, searchStartDate, searchEndDate) => {
  try {
    const start = new Date(searchStartDate);
    const end = new Date(searchEndDate);
    const unavailableRanges = [];
    
    // Query all orders in the date range
    const ordersRef = collection(db, 'orders');
    const snapshot = await getDocs(ordersRef);
    
    console.log(`🔍 Checking ${snapshot.docs.length} total orders for product: ${productId}`);
    
    for (const doc of snapshot.docs) {
      const orderData = doc.data();
      
      // Skip cancelled, failed, refunded, or draft orders
      const excludedStatuses = [
        'cancelled', 'Cancelled', 
        'failed', 'Failed',
        'refunded', 'Refunded',
        'draft', 'Draft',
        'error', 'Error'
      ];
      if (excludedStatuses.includes(orderData.status)) {
        console.log(`⏭️ Skipping order ${doc.id} with status: ${orderData.status}`);
        continue;
      }
      
      // Enhanced product matching (same as checkProductAvailability)
      const hasProduct = orderData.items && orderData.items.some(item => {
        // Method 1: Direct ID match (for system-generated orders)
        if (item.id === productId || item.productId === productId) {
          console.log(`✅ Found product match by ID: ${item.id || item.productId} in order ${doc.id}`);
          return true;
        }
        
        // Method 2: Name-based matching for manual orders
        if (item.name && typeof productId === 'string') {
          const itemNameLower = item.name.toLowerCase();
          const productIdLower = productId.toLowerCase();
          
          // Direct name match
          if (itemNameLower === productIdLower) {
            console.log(`✅ Found product match by exact name: "${item.name}" in order ${doc.id}`);
            return true;
          }
          
          // Enhanced Blue Marble matching for both manual and system orders
          if (itemNameLower.includes('blue marble') && 
              (productIdLower.includes('blue marble') || productIdLower.includes('blue_marble'))) {
            console.log(`✅ Found Blue Marble product match: "${item.name}" in order ${doc.id}`);
            return true;
          }
        }
        
        return false;
      });
      
      if (!hasProduct) {
        console.log(`❌ No product match in order ${doc.id}`);
        continue;
      }
      
      // Get the rental period from the order
      const orderRentalPeriod = getRentalPeriodFromOrder(orderData);
      
      if (!orderRentalPeriod.startDate || !orderRentalPeriod.endDate) {
        console.log(`⚠️ No valid rental period found in order ${doc.id}`);
        continue;
      }
      
      let orderStart, orderEnd;
      
      // Handle different date formats
      if (orderRentalPeriod.startDate.toDate) {
        orderStart = orderRentalPeriod.startDate.toDate();
      } else if (orderRentalPeriod.startDate instanceof Date) {
        orderStart = orderRentalPeriod.startDate;
      } else if (typeof orderRentalPeriod.startDate === 'string') {
        orderStart = new Date(orderRentalPeriod.startDate);
      } else {
        continue;
      }
      
      if (orderRentalPeriod.endDate.toDate) {
        orderEnd = orderRentalPeriod.endDate.toDate();
      } else if (orderRentalPeriod.endDate instanceof Date) {
        orderEnd = orderRentalPeriod.endDate;
      } else if (typeof orderRentalPeriod.endDate === 'string') {
        orderEnd = new Date(orderRentalPeriod.endDate);
      } else {
        continue;
      }
      
      // Check if the order period overlaps with our search range
      if ((orderStart <= end) && (orderEnd >= start)) {
        console.log(`🔒 Found conflicting order ${doc.id} for dates ${orderStart.toISOString().split('T')[0]} to ${orderEnd.toISOString().split('T')[0]}`);
        unavailableRanges.push({
          startDate: orderStart.toISOString().split('T')[0],
          endDate: orderEnd.toISOString().split('T')[0],
          orderId: doc.id
        });
      }
    }
    
    console.log(`📊 Found ${unavailableRanges.length} conflicting date ranges for ${productId}`);
    return unavailableRanges;
    
  } catch (error) {
    console.error('Error getting unavailable date ranges:', error);
    throw new Error('Failed to get unavailable dates. Please try again.');
  }
};

// Get unavailable individual dates (for backward compatibility and DatePicker exclusion)
export const getUnavailableDates = async (productId, startDate, endDate, location = 'All') => {
  try {
    // Get unavailable dates from existing bookings
    const ranges = await getUnavailableDateRanges(productId, startDate, endDate);
    const unavailableDates = new Set();
    
    // Convert date ranges to individual dates with precise formatting
    ranges.forEach(range => {
      console.log(`📅 Processing booking range: ${range.startDate} to ${range.endDate}`);
      
      // Use simple date parsing to avoid timezone issues
      const [startYear, startMonth, startDay] = range.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = range.endDate.split('-').map(Number);
      
      const start = new Date(startYear, startMonth - 1, startDay); // Month is 0-indexed
      const end = new Date(endYear, endMonth - 1, endDay);
      
      const current = new Date(start);
      while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        console.log(`  ➕ Adding unavailable date from booking: ${dateString}`);
        unavailableDates.add(dateString);
        current.setDate(current.getDate() + 1);
      }
    });
    
    // Get blocked dates from admin-blocked days
    try {
      const blockedDates = await getBlockedDatesArray(startDate, endDate, location);
      console.log(`🚫 Found ${blockedDates.length} blocked dates:`, blockedDates);
      
      blockedDates.forEach(date => {
        console.log(`  ➕ Adding blocked date: ${date}`);
        unavailableDates.add(date);
      });
    } catch (error) {
      console.warn('Error getting blocked dates, continuing without them:', error);
      // Continue without blocked dates rather than failing completely
    }
    
    const result = Array.from(unavailableDates).sort();
    console.log(`📊 Final unavailable dates (bookings + blocked): [${result.join(', ')}]`);
    return result;
    
  } catch (error) {
    console.error('Error getting unavailable dates:', error);
    throw new Error('Failed to get unavailable dates. Please try again.');
  }
};

// Validate booking request to prevent double booking for date ranges
export const validateBookingRequest = async (productId, startDate, endDate, excludeOrderId = null) => {
  try {
    const availability = await checkProductAvailability(productId, startDate, endDate);
    
    // If there are conflicts, check if any are from a different order
    if (!availability.available && availability.conflicts.length > 0) {
      // If we're updating an existing order, exclude it from conflict checking
      if (excludeOrderId) {
        const relevantConflicts = availability.conflicts.filter(
          conflict => conflict.orderId !== excludeOrderId
        );
        
        return {
          valid: relevantConflicts.length === 0,
          conflicts: relevantConflicts,
          message: relevantConflicts.length > 0 
            ? `This product is already booked during the selected rental period.`
            : 'Booking is valid.'
        };
      }
      
      return {
        valid: false,
        conflicts: availability.conflicts,
        message: `This product is already booked during the selected rental period. Please choose different dates.`
      };
    }
    
    return {
      valid: true,
      conflicts: [],
      message: 'Booking is available for the selected period.'
    };
    
  } catch (error) {
    console.error('Error validating booking request:', error);
    return {
      valid: false,
      conflicts: [],
      message: 'Unable to validate booking. Please try again.'
    };
  }
};

// Helper function to check if a date range is valid
export const validateDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();
  
  // Enforce 24-hour minimum booking buffer
  const minBookingTime = new Date();
  minBookingTime.setTime(minBookingTime.getTime() + (24 * 60 * 60 * 1000)); // Add exactly 24 hours
  
  // Check if booking is at least 24 hours in advance
  if (start < minBookingTime) {
    return {
      valid: false,
      message: 'Bookings must be made at least 24 hours in advance. Please select a later date.'
    };
  }
  
  // Check if end date is after start date (allow same-day rentals)
  if (end < start) {
    return {
      valid: false,
      message: 'End date must be after start date.'
    };
  }
  
  // Check if rental period is reasonable (e.g., not more than 30 days)
  const maxRentalDays = 30;
  const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  
  if (daysDiff > maxRentalDays) {
    return {
      valid: false,
      message: `Rental period cannot exceed ${maxRentalDays} days.`
    };
  }
  
  return {
    valid: true,
    message: 'Date range is valid.'
  };
}; 